import React, { forwardRef } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import {
  type ComponentSize,
  type ComponentVariant,
  type ComponentAppearance,
} from 'src/modules/shared/types/component.type';
import Button from '../../../atoms/buttons/button';
import Dropdown, {
  type DropdownItem,
  type DropdownPlacement,
} from '../dropdown';

interface SplitButtonDropdownProps {
  label: string;
  onClick: () => void;
  items: DropdownItem[];
  className?: string;
  mainButtonClassName?: string;
  size?: ComponentSize;
  variant?: ComponentVariant;
  appearance?: ComponentAppearance;
  icon?: React.ReactNode;
  disabled?: boolean;
  mainButtonDisabled?: boolean;
  loading?: boolean;
  placement?: DropdownPlacement;
}

export const SplitButtonDropdown = forwardRef<
  HTMLDivElement,
  SplitButtonDropdownProps
>(
  (
    {
      label,
      onClick,
      items,
      className = '',
      mainButtonClassName = '',
      size = 'md',
      variant = 'primary',
      appearance = 'filled',
      icon,
      disabled = false,
      mainButtonDisabled,
      loading = false,
      placement = 'bottom-end',
    },
    ref
  ) => {
    const buttonSizeToDropdownSize: Record<ComponentSize, string> = {
      lg: 'h-10',
      md: 'h-[34px]',
      sm: 'h-7',
      xs: 'h-[22px]',
      '2xs': 'h-4',
    };

    const isMainButtonDisabled = mainButtonDisabled ?? disabled;

    const handler = (
      <Button
        appearance={appearance}
        variant={variant}
        size={size}
        className={`${buttonSizeToDropdownSize[size]} px-0.5 border-l rounded-l-none ${className}`}
        disabled={disabled}
        loading={loading}
      >
        <ChevronDownIcon className="h-4 w-4" />
      </Button>
    );

    return (
      <div ref={ref} className="inline-flex">
        <Button
          appearance={appearance}
          variant={variant}
          size={size}
          onClick={onClick}
          className={`rounded-r-none border-r-0 p-1.5 ${mainButtonClassName}`}
          disabled={isMainButtonDisabled}
          loading={loading}
        >
          <div className="flex items-center justify-center gap-2">
            {icon}
            <span>{label}</span>
          </div>
        </Button>
        <Dropdown
          items={items}
          handler={handler}
          size={size}
          placement={placement}
        />
      </div>
    );
  }
);

SplitButtonDropdown.displayName = 'SplitButtonDropdown';
